import { MutableRefObject, useEffect } from 'react';

type Props = {
  wrapperRef: MutableRefObject<HTMLDivElement | null>;
  marqueeRef: MutableRefObject<HTMLDivElement | null>;
  scrollPositionRef: MutableRefObject<number>;
  isInteractionActiveRef: MutableRefObject<boolean>;
  hasManualScrollRef: MutableRefObject<boolean>;
  isEnabled: boolean;
};

const WHEEL_LINE_HEIGHT_PX = 16;

const useWheelMarqueeScroll = ({
  wrapperRef,
  marqueeRef,
  scrollPositionRef,
  isInteractionActiveRef,
  hasManualScrollRef,
  isEnabled,
}: Props): void => {
  useEffect(() => {
    const currentScrollPositionRef = scrollPositionRef;
    const currentInteractionActiveRef = isInteractionActiveRef;
    const currentHasManualScrollRef = hasManualScrollRef;
    const wrapper = wrapperRef.current;
    const marquee = marqueeRef.current;
    if (!wrapper || !marquee || !isEnabled) return undefined;

    const handleWheel = (event: WheelEvent) => {
      if (!currentInteractionActiveRef.current) return;
      event.preventDefault();

      const halfWidth = marquee.scrollWidth / 2;
      if (!halfWidth) return;

      let delta = event.deltaX !== 0 ? event.deltaX : event.deltaY;
      if (event.deltaMode === WheelEvent.DOM_DELTA_LINE) {
        delta *= WHEEL_LINE_HEIGHT_PX;
      }

      let newPosition = currentScrollPositionRef.current - delta;
      while (newPosition < -halfWidth) newPosition += halfWidth;
      while (newPosition > 0) newPosition -= halfWidth;

      currentHasManualScrollRef.current = true;
      currentScrollPositionRef.current = newPosition;
      marquee.style.animation = 'none';
      marquee.style.transform = `translateX(${newPosition}px)`;
    };

    wrapper.addEventListener('wheel', handleWheel, { passive: false });
    return () => wrapper.removeEventListener('wheel', handleWheel);
  }, [hasManualScrollRef, isEnabled, isInteractionActiveRef, marqueeRef, scrollPositionRef, wrapperRef]);
};

export default useWheelMarqueeScroll;
